/**
 * 自動ログアウトの見張りの状態（#189、docs/76）。api.ts と components/AutoLogout.tsx が共有する（ここは api を import しない＝輪にしない）。
 *
 *   ・人の操作：キー・クリック・タッチ・ホイール（タブに戻っただけ・定期の読み直しは操作に数えない＝lib/activity.ts とは別）。
 *     操作から USER_ACTION_WINDOW_MS（15秒）以内に出た要求だけ X-User-Action: 1（API はそのときだけ操作の時刻を進める）
 *   ・期限：毎回の答えのヘッダー x-gb-idle-left（秒）＋受け取った時刻。帯・止めの画面はこれで決める（端末の時計がずれても同じ）
 *   ・切れた：API の 401 SESSION_IDLE（idle）か、ログインしていた人の 401 AUTH_REQUIRED（expired）。
 *     登録された入力の退避（stash）を先に呼んでから、止めの画面（idle）かログインの画面（expired）へ
 */
import { USER_ACTION_HEADER, USER_ACTION_WINDOW_MS, safeNextPath } from "@genbaryoku/contract";

/** passive の印（api の headers に混ぜる。send が外して X-User-Action を付けない）。帯の予定の確かめ・タブ復帰の /me・つながりの確かめ */
export const PASSIVE_HEADER = "x-gb-passive";
export const PASSIVE: Record<string, string> = { [PASSIVE_HEADER]: "1" };
/** 押したボタンの要求（「ログイン継続」）：操作の窓の外でも X-User-Action を付ける */
export const USER_ACTION: Record<string, string> = { [USER_ACTION_HEADER]: "1" };

let lastInputAt = 0;
const inputListeners = new Set<() => void>();
const onInput = () => {
  lastInputAt = Date.now();
  inputListeners.forEach((f) => f());
};
if (typeof window !== "undefined") {
  for (const ev of ["pointerdown", "keydown", "wheel", "touchstart"] as const) window.addEventListener(ev, onInput, { capture: true, passive: true });
}
/** 人の操作があったら呼ぶ（帯を消す・延長を送る） */
export function onUserInput(f: () => void): () => void {
  inputListeners.add(f);
  return () => inputListeners.delete(f);
}

/** この要求に X-User-Action を付けるか（passive の印は外す） */
export function actionHeaders(headers: Record<string, string> | undefined): Record<string, string> {
  const h = { ...headers };
  const passive = h[PASSIVE_HEADER] === "1";
  delete h[PASSIVE_HEADER];
  if (!passive && !h[USER_ACTION_HEADER] && Date.now() - lastInputAt < USER_ACTION_WINDOW_MS) h[USER_ACTION_HEADER] = "1";
  return h;
}

/* ---- 期限 ---- */
type State = {
  /** 無操作で切れる時刻（ms。null＝まだ分からない・ログインしていない） */
  deadline: number | null;
  /** 切れた（idle＝止めの画面／expired＝ログインの画面へ移る途中）。path＝切れたときの画面 */
  ended: null | { reason: "idle" | "expired"; path: string };
};
let state: State = { deadline: null, ended: null };
/** 操作の印を付けた要求が最後に通った時刻（延長を送るかの目安） */
let lastActionSentAt = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((f) => f());
export function subscribe(f: () => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}
export const snapshot = (): State => state;
export const lastActionSent = (): number => lastActionSentAt;

/**
 * ログインしているか（ME があるか）。AutoLogoutHost が ME から、setMe（auth.tsx）がログイン・会社の切替のたびに入れる。
 * 401 を「切れた」と見るのはログインしていたときだけ。ログインした時刻（loggedInSince）より前に出た要求の 401 は見ない
 * （ログイン前に出た /me の 401 があとから届いても、ログインの画面に戻さない＝#176 B1 と同じ競り合い）
 */
let loggedIn = false;
let loggedInSince = 0;
export function setLoggedIn(v: boolean, fresh = false): void {
  if (v && (!loggedIn || fresh)) loggedInSince = Date.now();
  loggedIn = v;
  if (!v && state.deadline !== null) {
    state = { ...state, deadline: null };
    emit();
  }
}
export const isLoggedIn = (): boolean => loggedIn;
/** その要求はログインより前に出たか（その答えの 401 は今のログインのことではない） */
export const sentBeforeLogin = (sentAt: number): boolean => loggedIn && sentAt < loggedInSince;

/** 自分でログアウトした時刻（ログインの画面に next を付けない＝次にログインした人を前の画面に戻さない） */
let loggedOutAt = 0;
export function markLoggedOut(): void {
  loggedOutAt = Date.now();
  rememberLoginNext(null);
}
export const justLoggedOut = (): boolean => Date.now() - loggedOutAt < 5000;

/** 答えのヘッダー x-gb-idle-left を受け取った（sentAction＝その要求に X-User-Action を付けた） */
export function noteIdleLeft(sec: number, sentAction: boolean): void {
  if (!Number.isFinite(sec) || state.ended) return;
  if (sentAction) lastActionSentAt = Date.now();
  const deadline = Date.now() + sec * 1000;
  // 1秒未満の揺れでは描き直さない
  if (state.deadline !== null && Math.abs(state.deadline - deadline) < 1000) return;
  state = { ...state, deadline };
  emit();
}

/* ---- 入力の保護（④）：画面が登録する ---- */
export type UnsavedGuard = {
  /** 帯を出すとき：書きかけを送る */
  flush: () => Promise<void>;
  /** 切れたとき：送れていない直しを端末に退避（同期で書く） */
  stash: () => void;
};
const guards = new Set<UnsavedGuard>();
export function registerUnsaved(g: UnsavedGuard): () => void {
  guards.add(g);
  return () => guards.delete(g);
}
export async function flushUnsaved(): Promise<void> {
  await Promise.allSettled([...guards].map((g) => g.flush()));
}

/** 切れた（api.ts が呼ぶ）。1回だけ：入力を退避してから止めの画面・ログインの画面へ（AutoLogoutHost が描く・移る） */
export function sessionLost(reason: "idle" | "expired"): void {
  if (state.ended) return;
  for (const g of guards) {
    try {
      g.stash();
    } catch {
      // 退避できなくても止めの画面は出す
    }
  }
  state = { deadline: null, ended: { reason, path: window.location.pathname + window.location.search } };
  emit();
}

/* ---- 退避した入力（localStorage。キーは gb.unsaved.<account>.<document>） ---- */
const STASH_PREFIX = "gb.unsaved.";
/** 退避を残す日数（それより古いものは消す） */
const STASH_DAYS = 7;
export const stashKey = (accountId: string, documentId: string) => `${STASH_PREFIX}${accountId}.${documentId}`;
export function writeStash(accountId: string, documentId: string, value: unknown): void {
  try {
    localStorage.setItem(stashKey(accountId, documentId), JSON.stringify({ at: Date.now(), value }));
  } catch {
    // 使えないブラウザ（プライベート・容量）では退避しない
  }
}
export function readStash<T>(accountId: string, documentId: string): T | null {
  try {
    const raw = localStorage.getItem(stashKey(accountId, documentId));
    if (!raw) return null;
    const j = JSON.parse(raw) as { at?: number; value?: T };
    if (typeof j.at !== "number" || Date.now() - j.at > STASH_DAYS * 86_400_000) {
      localStorage.removeItem(stashKey(accountId, documentId));
      return null;
    }
    return j.value ?? null;
  } catch {
    return null;
  }
}
export function removeStash(accountId: string, documentId: string): void {
  try {
    localStorage.removeItem(stashKey(accountId, documentId));
  } catch {
    // 何もしない
  }
}
/** ログインした人と違う人の退避と、古い退避を消す（別の人の入力を復元しない・端末に残さない） */
export function pruneStashes(accountId: string): void {
  try {
    const drop: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k?.startsWith(STASH_PREFIX)) continue;
      const owner = k.slice(STASH_PREFIX.length).split(".")[0];
      let old = false;
      try {
        const at = (JSON.parse(localStorage.getItem(k) ?? "{}") as { at?: unknown }).at;
        old = typeof at !== "number" || Date.now() - at > STASH_DAYS * 86_400_000;
      } catch {
        old = true;
      }
      if (owner !== accountId || old) drop.push(k);
    }
    drop.forEach((k) => localStorage.removeItem(k));
  } catch {
    // 何もしない
  }
}

/* ---- ログインのあとに戻る画面（next）。ログインの画面が覚え、ログイン・会社を選ぶ・Google／Apple の続きが使う ---- */
const NEXT_KEY = "gb.login.next";
export function rememberLoginNext(path: string | null): void {
  try {
    if (path) sessionStorage.setItem(NEXT_KEY, path);
    else sessionStorage.removeItem(NEXT_KEY);
  } catch {
    // 使えないブラウザでは覚えない
  }
}
export function takeLoginNext(): string | null {
  try {
    const v = sessionStorage.getItem(NEXT_KEY);
    sessionStorage.removeItem(NEXT_KEY);
    return v;
  } catch {
    return null;
  }
}
/** ログインのあとに開く画面（#189：/login?next=… で覚えたもの。同じサイトの道だけ。無ければホーム）。1回読んだら消える */
export const afterLoginPath = (): string => safeNextPath(takeLoginNext()) ?? "/home";
